import type { FC } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Portal } from '@lt/components';
import styled from 'styled-components';

import PlaneSearchIcon from '@lt/components/icons-react/userflow/PlaneSearch';
import SuccessIcon from '@lt/components/icons-react/userflow/SuccessMarkDone';
import CloseIcon from '@lt/components/icons-react/Close';

import useTimeout from 'src/hooks/useTimeout';
import { HEADER_OFFSET } from 'src/constants/header';
import { useTranslation } from '@lt/localization';
import { scrollToElement } from 'src/utils/domUtils';
import { getCountLabel, getProgress } from './utils';

interface FlightsProgressbarProps {
  flightsCount: number;
  currentToursCount: number;
  totalToursCount: number;
  className?: string;
  isPriceFell?: boolean;
  cheapestPrice: number;
  topMargin?: number;
  startProgress?: number;
  isFetchingDone: boolean;
  setStickyProgressbar: (visible: boolean) => void;
  isAltActualizationFinished: boolean;
}

const FlightsProgressbar: FC<FlightsProgressbarProps> = ({
  flightsCount,
  currentToursCount,
  totalToursCount,
  className,
  isPriceFell,
  cheapestPrice,
  topMargin = HEADER_OFFSET,
  startProgress = 30,
  isFetchingDone,
  setStickyProgressbar = () => null,
  isAltActualizationFinished,
}) => {
  const { t } = useTranslation();

  const [visible, setVisible] = useState(true);

  const [isSticky, setIsSticky] = useState(false);

  const someFlightsFetched = Boolean(flightsCount);

  const relElRef = useRef<HTMLDivElement>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      setIsSticky(!entries[0].isIntersecting);
    },
    [],
  );

  const [isPriceHighlighted, setIsPriceHighlighted] = useState(false);

  useEffect(() => {
    if (isPriceFell) {
      setIsPriceHighlighted(true);
    }
  }, [isPriceFell]);

  useEffect(() => {
    if (!isAltActualizationFinished) {
      setVisible(true);
    }
  }, [isAltActualizationFinished]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: `-${topMargin}px 0px 10000px 0px`,
      threshold: [0.5],
    });

    if (relElRef.current) {
      observer.observe(relElRef.current);
    }

    return () => observer.disconnect();
  }, [handleIntersection, topMargin]);

  useTimeout(() => isFetchingDone && setVisible(false), 2000, [isFetchingDone]);

  const handleClick = () => {
    const scrollTarget = document.querySelector('.checkout-flight-cheapest'); // TODO: Сделать через react ref'ы
    if (isPriceHighlighted) {
      scrollToElement(scrollTarget as HTMLElement);
    }
  };

  setStickyProgressbar(visible);

  if (!visible) return null;

  const InsideComponent = (
    <>
      <StyledInfoPart>
        <StyledIconContainer>
          {isFetchingDone ? (
            <SuccessIcon height={14} width={14} />
          ) : (
            <PlaneSearchIcon height={16} width={16} />
          )}
        </StyledIconContainer>

        {someFlightsFetched ? (
          <>
            <span>{getCountLabel(flightsCount)}</span>
            <StyledPrice
              $isHighlighted={isPriceHighlighted}
              onClick={handleClick}
            >
              <span>{`${cheapestPrice} ₽`}</span>
            </StyledPrice>
          </>
        ) : (
          <span>{t('checkout.flight.progressbar.title')}</span>
        )}
        <StyledCloseButtonContainer>
          {isFetchingDone && (
            <StyledCloseIcon
              height={10}
              width={10}
              onClick={() => setVisible(false)}
            />
          )}
        </StyledCloseButtonContainer>
      </StyledInfoPart>
      <StyledProgressbar>
        <StyledProgress
          progress={getProgress(
            currentToursCount,
            totalToursCount,
            startProgress,
            isFetchingDone,
          )}
          isFetchingDone={isFetchingDone}
        />
      </StyledProgressbar>
    </>
  );

  return (
    <>
      {isSticky ? (
        <Portal>
          <StyledContainer
            className={className}
            variant="sticky"
            topMargin={topMargin}
          >
            {InsideComponent}
          </StyledContainer>
        </Portal>
      ) : null}

      <StyledContainer className={className} ref={relElRef} variant="relative">
        {InsideComponent}
      </StyledContainer>
    </>
  );
};

const StyledContainer = styled.div<{
  variant: 'sticky' | 'relative';
  topMargin?: number;
}>`
  background-color: #ffffff;
  width: 100%;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 2;
  margin-bottom: 16px;

  ${({ variant, topMargin }) =>
    variant === 'sticky' &&
    ` position: fixed;
      top: ${topMargin}px;`}
`;

const StyledIconContainer = styled.div`
  margin-right: 4px;
  display: flex;
  width: 14px;
`;

const StyledInfoPart = styled.span`
  position: relative;
  padding: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledCloseButtonContainer = styled.div`
  position: absolute;
  right: 4px;
`;

const StyledCloseIcon = styled(CloseIcon)`
  cursor: pointer;
`;

const StyledPrice = styled.span<{ $isHighlighted?: boolean }>`
  color: #007aff;
  margin-left: 4px;

  ${({ $isHighlighted }) =>
    $isHighlighted &&
    `color: black;
    background-color: #F8E71C;
    border-radius: 4px;
    padding: 0 4px;
    cursor: pointer;
  `}
`;

const StyledProgressbar = styled.div`
  width: 100%;
  height: 2px;
  border-radius: 0 0 5px 5px;
  background-color: #e6e9ee;
`;

const StyledProgress = styled.div<{
  progress: number;
  isFetchingDone: boolean;
}>`
  float: left;
  width: ${({ progress }) => progress}%;
  background-color: ${({ isFetchingDone }) =>
    isFetchingDone ? '#28BD61' : '#007aff'};
  height: 2px;
  border-radius: 0 ${({ progress }) => (progress === 100 ? 0 : '4px')} 4px 4px;
  transition: all 1s ease-out;
`;

export default FlightsProgressbar;
